import React, { useEffect, useState } from "react";
import { useGetProductsDetailsQuery } from "../../redux/api/productsApi";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "../layout/Loader";
import { useDispatch, useSelector } from "react-redux";
import { setCartItem } from "../../redux/features/cartSlice";
import NewReviews from "../reviews/NewReviews";
import ListReviews from "../reviews/ListReviews";


const ProductDetails = () => {
    const params = useParams();
    const dispatch = useDispatch();

    const [quantity, setQuantity] = useState(1);
    const [buyPerPiece, setBuyPerPiece] = useState(false);
    const [selectedColors, setSelectedColors] = useState([]);
    const [activeImg, setActiveImg] = useState("/images/default_product.png");
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(-1);

    const { data, isLoading, error, isError, refetch } = useGetProductsDetailsQuery(
        params?.id
    );
    const product = data?.product;

    const { isAuthenticated } = useSelector((state) => state.auth);




    // Set the first product image when product changes
    useEffect(() => {
        if (product?.images && product.images.length > 0) {
            setActiveImg(product.images[0]?.url || "/images/default_product.png");
        }
    }, [product]);

    // Handle errors
    useEffect(() => {
        if (isError) {
            toast.error(error?.data?.message || "Something went wrong");
        }
    }, [isError, error]);


    // ✅ Increase qty
    const increaseQty = () => {
        if (!product) return;
        if (quantity >= effectiveStock) return; // stop at stock (variant-aware)
        setQuantity((prev) => prev + 1);
    };

    // ✅ Decrease qty
    const decreaseQty = () => {
        if (quantity <= 1) return;
        setQuantity((prev) => prev - 1);
    };

    const hasVariants = Array.isArray(product?.variants) && product.variants.length > 0;
    const selectedVariant = (hasVariants && selectedVariantIdx >= 0)
        ? product?.variants?.[selectedVariantIdx]
        : null;
    const variantPrice = selectedVariant ? Number(selectedVariant?.price || 0) : 0;
    const basePrice = selectedVariant ? variantPrice : Number(product?.price || 0);
    const effectiveStock = selectedVariant ? Number(selectedVariant?.stock || 0) : Number(product?.stock || 0);
    const effectivePrice = (product?.perPieceEnabled && buyPerPiece)
        ? Number(product?.perPiecePrice || 0)
        : basePrice;

    const setItemToCart = () => {
        const cartItem = {
            product: product?._id,
            name: product?.name,
            price: effectivePrice,
            image: product?.images[0]?.url,
            stock: product?.stock,
            quantity,
            perPieceSelected: !!(product?.perPieceEnabled && buyPerPiece),
            ...(selectedColors?.length ? { colors: selectedColors } : {}),
            ...(hasVariants && selectedVariantIdx >= 0 ? {
                variant: {
                    index: selectedVariantIdx,
                    name: product?.variants?.[selectedVariantIdx]?.name,
                    price: product?.variants?.[selectedVariantIdx]?.price,
                }
            } : {}),
        }
        dispatch(setCartItem(cartItem));
        toast.success("Item added to Cart")
    }

    if (isLoading) return <Loader />;

    return (
        <>
        <div className="main-content">
            <div className="row justify-content-center my-5 product-details-row">
                {/* Product Image Section */}
                <div className="col-12 col-lg-5 mb-5">
                    <div className="card shadow-sm p-3 rounded-4 product-card">
                        {/* Main Image */}
                        <img
                            className="img-fluid rounded mx-auto d-block"
                            src={activeImg}
                            alt={product?.name}
                            width="350"
                            height="400"
                        />

                        {/* Thumbnail Gallery */}
                        <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                            {product?.images && product.images.length > 0 ? (
                                product.images.map((img, index) => (
                                    <img
                                        key={index}
                                        className={`border rounded p-2 cursor-pointer ${activeImg === (img?.url || `/images/${img}`)
                                            ? "border-primary"
                                            : ""
                                            }`}
                                        height="80"
                                        width="80"
                                        src={img?.url || `/images/${img}`}
                                        alt={`Thumbnail ${index + 1}`}
                                        onClick={() =>
                                            setActiveImg(img?.url || `/images/${img}`)
                                        }
                                    />
                                ))
                            ) : (
                                <>
                                    <img
                                        className="border rounded p-2 cursor-pointer"
                                        height="80"
                                        width="80"
                                        src="/images/default_product.png"
                                        alt="Default Thumbnail"
                                        onClick={() => setActiveImg("/images/default_product.png")}
                                    />
                                    <img
                                        className="border rounded p-2 cursor-pointer"
                                        height="80"
                                        width="80"
                                        src="/images/default_product.png"
                                        alt="Default Thumbnail"
                                        onClick={() => setActiveImg("/images/default_product.png")}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Product Details Section */}
                <div className="col-12 col-lg-6">
                    <div className="card shadow-sm p-3 rounded-4 product-card">
                        <h3 className="mb-2 fw-bold" style={{ color: "#e04f6ecd" }}>{product?.name}</h3>
                        <p id="product_id" className="text small" style={{ color: "#e04f6ecd" }}>
                            Product # {product?._id}
                        </p>
                        <hr />

                        {/* Ratings */}
                        <div className="ratings d-flex align-items-center mt-3 mb-3">
                            {[...Array(5)].map((_, i) => (
                                <i
                                    key={i}
                                    className={`fa fa-star ${i < (product?.ratings || 0)
                                        ? "text-warning"
                                        : "text-secondary"
                                        }`}
                                ></i>
                            ))}
                            <span id="no-of-reviews" className="ms-2 text-muted">
                                ({product?.numOfReviews} Review
                                {product?.numOfReviews !== 1 ? "s" : ""})
                            </span>
                        </div>

                        {/* Variants (if any) */}
                        {hasVariants && (
                            <div className="mb-3">
                                <h6 className="fw-semibold mb-2" style={{ color: "#e04f6ecd" }}>Choose Option:</h6>
                                <div className="d-flex flex-wrap gap-2">
                                    {product.variants.map((v, i) => {
                                        const selected = i === selectedVariantIdx;
                                        const out = Number(v?.stock || 0) <= 0;
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                aria-pressed={selected}
                                                aria-disabled={out}
                                                disabled={out}
                                                onClick={() => !out && setSelectedVariantIdx(selected ? -1 : i)}
                                                className="bg-white"
                                                title={out ? "Out of stock" : undefined}
                                                style={{
                                                    padding: "6px 12px",
                                                    borderRadius: 999,
                                                    border: selected ? "2px solid #e91e63" : "1px solid #ddd",
                                                    background: out ? "#f8f9fa" : "#fff",
                                                    color: out ? "#999" : "#e04f6e",
                                                    boxShadow: selected ? "0 2px 8px rgba(233,30,99,.15)" : "0 1px 4px rgba(0,0,0,.06)",
                                                    transition: "all .15s ease-in-out",
                                                    cursor: out ? "not-allowed" : "pointer",
                                                    opacity: out ? 0.6 : 1,
                                                }}
                                            >
                                                {v.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Price */}
                        <h4 id="product_price" className="price d-flex align-items-center gap-2">
                            <span>PKR {effectivePrice}</span>
                            {hasVariants && selectedVariantIdx >= 0 && (
                                <span className="badge" style={{ background: "#ffe6ec", color: "#e04f6e", border: "1px solid #f3c0cc" }}>
                                    {product?.variants?.[selectedVariantIdx]?.name}
                                </span>
                            )}
                        </h4>

                        {/* Per-piece toggle */}
                        {product?.perPieceEnabled && (
                            <div
                                className={"rounded-4 p-3 shadow-sm mb-3"}
                                style={{
                                    background: buyPerPiece ? "#ffe6ecff" : "#fff",
                                    outline: buyPerPiece ? "2px solid #e04f6ecd" : "none",
                                    outlineOffset: "0px",
                                    borderRadius: "1rem",
                                    transition: "background .2s ease, outline-color .2s ease",
                                }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        aria-pressed={buyPerPiece}
                                        aria-label="Toggle buy per piece"
                                        onClick={() => setBuyPerPiece(v => !v)}
                                        className="btn p-0 border-0 bg-transparent"
                                        style={{ cursor: "pointer" }}
                                    >
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 22,
                                                height: 22,
                                                borderRadius: "6px",
                                                border: `2px solid var(--color-primary)`,
                                                background: buyPerPiece ? "var(--color-primary)" : "transparent",
                                                color: "#fff",
                                                transition: "all .2s ease"
                                            }}
                                        >
                                            {buyPerPiece ? <i className="fa fa-check" aria-hidden="true"></i> : null}
                                        </span>
                                    </button>
                                    <span
                                        role="button"
                                        onClick={() => setBuyPerPiece(v => !v)}
                                        className="fw-semibold"
                                        style={{ color: "#e04f6ecd", cursor: "pointer" }}
                                    >
                                        Buy per piece
                                    </span>
                                    {buyPerPiece && (
                                        <span className="badge" style={{ background: "#e04f6ecd", color: "#fff" }}>Selected</span>
                                    )}
                                </div>
                                <small className="text ms-4" style={{ color: "#e04f6ecd" }}>Price per piece: PKR {product?.perPiecePrice}</small>
                            </div>
                        )}

                        {/* Choose Color */}
                        {Array.isArray(product?.colors) && product.colors.length > 0 && (
                            <div className="mb-3">
                                <h6 className="fw-semibold" style={{ color: "#e04f6ecd" }}>Choose:</h6>
                                <div className="d-flex flex-wrap gap-2">
                                    {product.colors.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => {
                                                setSelectedColors((prev) => {
                                                    const exists = prev.includes(c);
                                                    if (exists) return prev.filter(x => x !== c);
                                                    return [...prev, c];
                                                });
                                            }}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                padding: "6px 10px",
                                                borderRadius: "999px",
                                                border: selectedColors.includes(c) ? "2px solid #e91e63" : "1px solid #ddd",
                                                background: "#fff",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <span style={{
                                                display: "inline-block",
                                                width: 18,
                                                height: 18,
                                                borderRadius: "50%",
                                                border: "1px solid rgba(0,0,0,0.15)",
                                                backgroundColor: c
                                            }} />
                                            <span style={{ fontSize: ".9rem" }}>{c}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Cart Section */}
                        <div className="d-flex align-items-center mb-4">
                            <div className="input-group w-auto">
                                <button className="btn btn-outline-danger" onClick={decreaseQty}>-</button>
                                <input
                                    type="number"
                                    className="form-control text-center count"
                                    value={quantity}
                                    readOnly
                                />
                                <button className="btn btn-outline-primary" onClick={increaseQty}>+</button>
                            </div>
                            <button
                                type="button"
                                id="View_btn"
                                className="btn View-btn  ms-auto"
                                disabled={effectiveStock <= 0}
                                onClick={setItemToCart}
                            >
                                Add to Cart
                            </button>
                        </div>

                        {/* Stock */}
                        <p className="mb-3" style={{ color: "#e04f6ecd" }}>
                            Status:{" "}
                            <span
                                id="stock_status"
                                className={effectiveStock > 0 ? "greenColor" : "redColor"}
                            >
                                {effectiveStock > 0 ? "In Stock" : "Out of Stock"}
                            </span>
                        </p>

                        {/* Description */}
                        <h5 style={{ color: "#e04f6ecd" }}>Description:</h5>
                        <p className="text" style={{ color: "#e04f6ecd", textAlign: "justify" }}>{product?.description}</p>

                        {/* Seller */}
                        <p id="product_seller" className="mb-3" style={{ color: "#e04f6ecd" }}>
                            Sold by: <strong>{product?.seller}</strong>
                        </p>

                        {/* Review Alert */}
                        {isAuthenticated && product?._id ? (
                            <NewReviews
                                productId={product?._id}
                                onSubmitted={async () => {
                                    try {
                                        await refetch();
                                    } finally {
                                        const el = document.getElementById('reviews_section');
                                        if (el) {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <div className="alert alert-danger my-4" role="alert">
                                <strong>Login to post your review.</strong>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        <div className="row mt-4 w-100" id="reviews_section" style={{ clear: 'both', marginBottom: '60px' }}>
            <div className="col-12">
                <ListReviews reviews={product?.reviews} />
            </div>
        </div>
        </>
    );
};

export default ProductDetails;
